import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, BehaviorSubject, filter, take } from 'rxjs';
import { AuthService } from '../services/auth.service';

let refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Only add token for /admin/* requests
  if (!req.url.includes('/admin/')) {
    return next(req);
  }

  // Skip token for auth endpoints that don't need it
  if (req.url.includes('/admin/auth/login') || req.url.includes('/admin/auth/refresh')) {
    return next(req);
  }

  const token = authService.getToken();
  const authReq = token ? addTokenToRequest(req, token) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handleUnauthorized(req, next, authService, router);
      }
      return throwError(() => error);
    }),
  );
};

function addTokenToRequest(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      'X-Auth-Token': `Bearer ${token}`,
    },
  });
}

function handleUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
) {
  if (authService.isRefreshingToken) {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(addTokenToRequest(req, token!))),
    );
  }

  authService.isRefreshingToken = true;
  refreshTokenSubject.next(null);

  return authService.refresh().pipe(
    switchMap((response) => {
      authService.isRefreshingToken = false;
      refreshTokenSubject.next(response.access_token);
      return next(addTokenToRequest(req, response.access_token));
    }),
    catchError((refreshError) => {
      authService.isRefreshingToken = false;
      router.navigate(['/login']);
      return throwError(() => refreshError);
    }),
  );
}
